import type { ScanPoolParcel, ScanPoolParcelRow } from '@/infrastructure/FulfillmentAdminClient';
import { WarningIcon } from '@/pages/fulfillment/components/UltimateIcons';

export interface PoolParcelNoticeProps {
  orderId: number;
  parcel: ScanPoolParcel;
  busy: boolean;
  onClose: (rows: ScanPoolParcelRow[]) => void;
}

/** The parked rows this order can take: a line of it fits the row, and the server named that line's sku to resolve to. */
export const closableRowsOf = (parcel: ScanPoolParcel, orderId: number): ScanPoolParcelRow[] =>
  parcel.rows.filter((row) => row.orderId === orderId && row.lineNo !== null && Boolean(row.lineSku));

/**
 * The box just scanned is still parked in the unrecognized pool (SSD-7442): its waybill was registered on this
 * order after the desk parked it. Nothing closes on its own, because a scan opens an order, not a line, so the
 * desk says it with one press; the pool row closes on the line it fits and the FC team's thread hears it.
 */
export function PoolParcelNotice({ orderId, parcel, busy, onClose }: PoolParcelNoticeProps) {
  const closable = closableRowsOf(parcel, orderId);
  const products = (count: number) => `${count} product${count === 1 ? '' : 's'}`;
  return (
    <div className="vo-pool-parcel" role="note" aria-label="Parked in the unrecognized pool">
      <div className="vo-pool-parcel-head">
        <WarningIcon />
        <b>
          This box is still in the unrecognized pool
          {parcel.temporaryLocation ? ` (parked at ${parcel.temporaryLocation})` : ''}
        </b>
      </div>
      <div className="vo-pool-parcel-body">
        <span>
          Its waybill is now on this order.{' '}
          {closable.length === parcel.rows.length
            ? products(closable.length)
            : `${closable.length} of ${products(parcel.rows.length)}`}{' '}
          {closable.length === 1 ? 'fits' : 'fit'} a line here
          {closable.length < parcel.rows.length ? '; the rest stay in the pool for the order team.' : '.'}
        </span>
        {closable.length > 0 && (
          <button type="button" className="btn btn-line" disabled={busy} onClick={() => onClose(closable)}>
            Close it on this order
          </button>
        )}
      </div>
    </div>
  );
}
